/**
 * Live / Staging, in the rail, directly under the app it belongs to.
 *
 * WHY IT IS HERE. Before this there was no switch at all: you reached staging
 * from a link buried in settings and came back from a link in the red banner,
 * which meant the two environments of one app were further apart in the
 * interface than two unrelated apps. The rail is where somebody already looks
 * to move around inside a project, so the environments belong in it, next to
 * the project's own name.
 *
 * WHY LINKS AND NOT A SEGMENTED CONTROL. patterns/SegmentedControl is for a
 * preference, and says so: its selected half is a surface step and never a
 * fill, because this product spends saturation on state alone. Which
 * environment you are looking at is not a preference, it is where you are, and
 * the staging half has to be able to carry the same red the banner does. These
 * are also real destinations: middle-click and "open in new tab" should work,
 * which rules out buttons.
 *
 * It keeps whatever screen you are on (lib/environments.ts's `sameScreenOn`),
 * because the reason to switch is nearly always to see the same thing on the
 * other side.
 */
import { Link, useLocation } from 'react-router';

import { useProjects } from '../../lib/auth/use-app-data';
import {
  hasTwoEnvironments,
  liveSlugOf,
  sameScreenOn,
  stagingSlugOf,
} from '../../lib/environments';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/environment-switch.css';

interface EnvironmentSwitchProps {
  slug: string | null;
}

export function EnvironmentSwitch({ slug }: EnvironmentSwitchProps) {
  const projects = useProjects();
  const location = useLocation();
  const project = slug ? projects.find((one) => one.slug === slug) : undefined;

  // Nothing to switch between until an app has a staging environment, and this
  // must not hint at one: creating it costs a second app and a second database,
  // and that decision belongs to the settings screen that explains the price.
  if (!project || !hasTwoEnvironments(project)) return null;

  const liveSlug = liveSlugOf(project);
  const stagingSlug = stagingSlugOf(project);
  if (!stagingSlug) return null;

  const onStaging = project.environment === 'staging';

  function track(target: 'production' | 'staging') {
    captureEvent('environment_switched', {
      project_slug: liveSlug,
      from: onStaging ? 'staging' : 'production',
      to: target,
    });
  }

  return (
    <div className="envswitch" role="group" aria-label={t('Environment')}>
      <Link
        className="envswitch__opt"
        data-current={!onStaging}
        data-env="production"
        aria-current={!onStaging ? 'page' : undefined}
        to={sameScreenOn(location.pathname, project.slug, liveSlug)}
        onClick={() => track('production')}
      >
        {t('Live')}
      </Link>
      <Link
        className="envswitch__opt"
        data-current={onStaging}
        data-env="staging"
        aria-current={onStaging ? 'page' : undefined}
        to={sameScreenOn(location.pathname, project.slug, stagingSlug)}
        onClick={() => track('staging')}
      >
        {t('Staging')}
      </Link>
    </div>
  );
}
